// @ts-ignore
import { getUserData } from "@/auth/getUserData";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import { AppInterface } from "@/interfaces/app.interface";
import {
    AgentAppDataInterface,
    AppDataInterface,
    ChannelAppDataInterface,
    ConversationAppDataInterface,
    MessageAppDataInterface,
    TemplateAppDataInterface,
} from "@/interfaces/appData.interface";
import axios from "@/libs/axios";
import { useChatsStore } from "@/store/useChatsStore";
import { useTagsStore } from "@/store/useTagsStore";
import { AppDataRequired } from "@/types/appDataRequired.type";
import { computed, readonly, Ref, ref, watch } from "@vue/composition-api";
import { storeToRefs } from "pinia";
import { useApps } from "@/composables/useApps";

const { activeChat } = storeToRefs(useChatsStore());

function getUserInfo(userId: number): Promise<AgentAppDataInterface> {
    return axios
        .get<AgentAppDataInterface>(`/users/${userId}?fields=[id,name,displayName,email,role]`)
        .then((result) => {
            // TODO: dont fetch settings when not asking for them
            // @ts-ignore
            delete result.data.settings;
            return result.data;
        });
}

function getChannels(withAuth: boolean): Promise<ChannelAppDataInterface[]> {
    const { inboxModuleType } = useInboxModuleType();

    /*if (inboxModuleType.value === InboxModuleType.EMAIL) {
        queryParams.params.s.platform = {
            $eq: InboxModuleType.EMAIL, // EQUAL email
        };
    } else {
        queryParams.params.s.platform = {
            $ne: InboxModuleType.EMAIL, // NOT email
        };
    }*/

    return axios
        .get<ChannelAppDataInterface[]>(`/channels`, {
            params: {
                fields: `[id,name,provider,platform,identity,active,createdAt,customerCareWindowLength${
                    withAuth ? ",auth" : ""
                }]`,
            },
        })
        .then((result) =>
            result.data.map((channel) => {
                // TODO: dont fetch settings when not asking for them
                // @ts-ignore
                delete channel.settings;
                return channel;
            })
        );
}

function getTemplates(): Promise<TemplateAppDataInterface[]> {
    return axios
        .get(`/templates`, {
            params: {
                join: ["channels"],
            },
        })
        .then((response) => response.data);
}

function getAssignedAgent(): Promise<AgentAppDataInterface | undefined> {
    if (activeChat.value.chat.assignedUserId == null) {
        return;
    }

    return getUserInfo(activeChat.value.chat.assignedUserId);
}

function getContact(withMetadata = false) {
    if (!activeChat.value) {
        return {};
    }

    return axios
        .get(`/contacts/${activeChat.value.chat.contactId}`, {
            params: withMetadata
                ? {
                      join: ["metadata||id,name,value"],
                  }
                : {},
        })
        .then((response) => response.data);
}

function getConversation(): ConversationAppDataInterface | undefined {
    if (activeChat.value === undefined) {
        return;
    }

    return {
        start: activeChat.value.chat.createdAt as unknown as string,
        status: activeChat.value.chat.status,
    };
}

function getMessages(): MessageAppDataInterface[] {
    if (!activeChat.value) {
        return [];
    }

    // Only pass on message events
    return activeChat.value.entries
        .filter((entry) => ["text", "attachment", "location"].includes(entry.type))
        .map((entry) => ({
            direction: entry.direction,
            time: entry.time,
            status: entry.status,
            message: "message" in entry ? entry.message : "",
        }));
}

function getTags(): string[] {
    const tagsStore = useTagsStore();

    return tagsStore.tags.map((tag) => tag.name) ?? [];
}

const state: Ref<AppDataInterface> = ref<AppDataInterface>({});
const activeAppId: Ref<number | null> = ref<number | null>(null);

const isLoading: Ref<boolean> = ref<boolean>(false);

const appModalIsActive: Ref<boolean> = ref<boolean>(false);
const appModalContent: Ref<unknown> = ref<unknown>(null);

export default function useAppData() {
    const { apps }: { apps: Ref<AppInterface[]> } = useApps();

    const setActiveApp = (appId: number | null) => (activeAppId.value = appId);

    const activeApp = computed(() => apps.value.find((app) => app.id === activeAppId.value));

    watch([activeAppId, activeChat], async () => {
        isLoading.value = true;
        // No active app, os no appData
        if (activeAppId.value == null) {
            state.value = {};

            isLoading.value = false;
            return;
        }

        const dataRequired = activeApp.value?.package?.saysimple?.dataRequired;

        // If there is no dataRequired set, appData should be an empty object
        if (dataRequired == null || dataRequired.length === 0) {
            state.value = {};
            isLoading.value = false;
            return;
        }

        const appData: AppDataInterface = {};

        let agentRequest;
        let assignedAgentRequest;
        let contactRequest;
        let channelsRequest;
        let templatesRequest;

        if (dataRequired.indexOf(AppDataRequired.AGENT) >= 0) {
            agentRequest = getUserInfo(getUserData().id);
        }

        if (dataRequired.indexOf(AppDataRequired.ASSIGNED_AGENT) >= 0) {
            assignedAgentRequest = getAssignedAgent();
        }

        if (dataRequired.indexOf(AppDataRequired.CONTACT) >= 0) {
            const withMetadata = dataRequired.indexOf(AppDataRequired.CONTACT_METADATA) >= 0;
            contactRequest = getContact(withMetadata);
        }

        if (dataRequired.indexOf(AppDataRequired.CHANNELS) >= 0) {
            const withChannelsAuth = dataRequired.indexOf(AppDataRequired.CHANNELS_AUTH) >= 0;
            channelsRequest = getChannels(withChannelsAuth);
        }

        if (dataRequired.indexOf(AppDataRequired.TEMPLATES) >= 0) {
            templatesRequest = getTemplates();
        }

        if (dataRequired.indexOf(AppDataRequired.CONVERSATION) >= 0) {
            appData[AppDataRequired.CONVERSATION] = getConversation();
        }

        if (dataRequired.indexOf(AppDataRequired.MESSAGES) >= 0) {
            appData[AppDataRequired.MESSAGES] = getMessages();
        }

        if (dataRequired.indexOf(AppDataRequired.TAGS) >= 0) {
            appData[AppDataRequired.TAGS] = getTags();
        }

        const [agent, assignedAgent, contact, channels, templates] = await Promise.all([
            agentRequest,
            assignedAgentRequest,
            contactRequest,
            channelsRequest,
            templatesRequest,
        ]);

        appData[AppDataRequired.AGENT] = agent;
        appData[AppDataRequired.ASSIGNED_AGENT] = assignedAgent;
        appData[AppDataRequired.CONTACT] = contact;
        appData[AppDataRequired.CHANNELS] = channels;
        appData[AppDataRequired.TEMPLATES] = templates;

        state.value = appData;
        isLoading.value = false;
    });

    const setAppModalActive = (appModalContentData: any) => {
        appModalContent.value = appModalContentData;
        appModalIsActive.value = true;
    };

    return {
        appData: readonly(state),
        setActiveApp,
        isLoading,

        // App Modal
        activeApp,
        appModalContent,
        setAppModalActive,
        appModalIsActive,
    };
}
